'use strict';

const CONFIG = {
    selectors: {
        desktopNav: '.global-nav-desktop',
        hasChildItems: '.child-nav-desktop__item.has-child',
        itemArrows: '.childNavButton',
        menuBtn: 'button.global-header__menu'
    },
    classes: {
        active: 'nav-desktop__item--active'
    }
};

class GlobalNavigationDesktop {
    constructor() {
        this.itemGroups = new Map();
        this.arrowButtons = new Map();
        this.menuBtn = null;
        this.activeItems = new Set();
    }

    init() {
        this.findElements();

        if (this.itemGroups.size === 0) {
            return;
        }

        this.convertArrowsToButtons();
        this.setupClickHandlers();
        this.setupKeyboardHandlers();
        this.setupReverseTabBehavior();
        this.setupForwardTabBehavior();
        this.setupMenuButton();
        this.initializeAriaStates();
    }

    findElements() {
        // First find the desktop navigation container
        const desktopNavContainer = document.querySelector(CONFIG.selectors.desktopNav);

        if (!desktopNavContainer) {
            return;
        }

        // Find all .child-nav-desktop__item.has-child elements within desktop nav
        const allHasChildItems = [...desktopNavContainer.querySelectorAll(CONFIG.selectors.hasChildItems)];

        // Group items by their parent UL, but only include levels 0, 1, and 2
        allHasChildItems.forEach(item => {
            const parentUl = item.closest('ul');
            const parentUlClass = parentUl.className;

            // Only include level-0, level-1, and level-2
            if (parentUlClass.includes('level-0') ||
                parentUlClass.includes('level-1') ||
                parentUlClass.includes('level-2')) {

                if (!this.itemGroups.has(parentUlClass)) {
                    this.itemGroups.set(parentUlClass, []);
                    this.arrowButtons.set(parentUlClass, []);
                }

                this.itemGroups.get(parentUlClass).push(item);

                // Find arrow within this item
                const arrow = item.querySelector(CONFIG.selectors.itemArrows);
                if (arrow) {
                    this.arrowButtons.get(parentUlClass).push(arrow);
                }
            }
        });

        this.menuBtn = document.querySelector(CONFIG.selectors.menuBtn);
    }

    convertArrowsToButtons() {
        // Convert all arrow spans to buttons
        this.arrowButtons.forEach(arrows => {
            arrows.forEach(arrow => {
                if (arrow.tagName.toLowerCase() !== 'button') {
                    const button = document.createElement('button');
                    button.className = arrow.className;
                    button.innerHTML = arrow.innerHTML;
                    button.setAttribute('type', 'button');
                    button.setAttribute('aria-expanded', 'false');

                    // Copy data attributes
                    Array.from(arrow.attributes).forEach(attr => {
                        if (attr.name.startsWith('data-')) {
                            button.setAttribute(attr.name, attr.value);
                        }
                    });

                    arrow.parentNode.replaceChild(button, arrow);
                }
            });
        });

        // Re-query buttons after conversion
        this.arrowButtons.clear();
        this.itemGroups.forEach((items, levelKey) => {
            const arrows = [];
            items.forEach(item => {
                const arrow = item.querySelector(CONFIG.selectors.itemArrows);
                if (arrow) {
                    arrows.push(arrow);
                }
            });
            this.arrowButtons.set(levelKey, arrows);
        });
    }

    initializeAriaStates() {
        // Set initial ARIA states - all submenus hidden
        this.itemGroups.forEach(items => {
            items.forEach(item => {
                const submenu = item.querySelector('ul');
                const button = item.querySelector('button.childNavButton');

                if (submenu && button) {
                    const submenuId = this.generateSubmenuId(item);
                    submenu.setAttribute('id', submenuId);
                    submenu.setAttribute('aria-hidden', 'true');
                    button.setAttribute('aria-controls', submenuId);
                    button.setAttribute('aria-expanded', 'false');
                }

                // Also handle nested lists within the item
                const nestedLists = item.querySelectorAll('.child-nav-desktop__nested');
                nestedLists.forEach(nestedList => {
                    nestedList.setAttribute('aria-hidden', 'true');
                });
            });
        });
    }

    generateSubmenuId(item) {
        const dataId = item.getAttribute('data-id');
        return `submenu-${dataId || Math.random().toString(36).substr(2, 9)}`;
    }

    setupClickHandlers() {
        this.arrowButtons.forEach((buttons, levelKey) => {
            buttons.forEach(button => {
                button.addEventListener('click', e => {
                    e.preventDefault();
                    e.stopPropagation();

                    const item = button.closest('.child-nav-desktop__item.has-child');
                    const isLevelZero = levelKey.includes('level-0');

                    this.toggleMenuItem(item, button, isLevelZero);
                });
            });
        });

        // Close menus when clicking outside
        document.addEventListener('click', e => {
            if (!e.target.closest('.global-nav-desktop')) {
                this.closeAllMenus();
            }
        });
    }

    setupKeyboardHandlers() {
        // Handle keyboard navigation
        this.arrowButtons.forEach(buttons => {
            buttons.forEach(button => {
                button.addEventListener('keydown', e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        button.click();
                    }

                    if (e.key === 'Escape') {
                        this.closeAllMenus();
                        button.focus();
                    }
                });
            });
        });

        // Handle navigation between items
        const desktopNav = document.querySelector(CONFIG.selectors.desktopNav);
        if (desktopNav) {
            desktopNav.addEventListener('keydown', e => {
                if (e.key === 'Escape') {
                    this.closeAllMenus();
                }
            });
        }
    }

    toggleMenuItem(item, button, isLevelZero) {
        const isCurrentlyActive = item.classList.contains(CONFIG.classes.active);

        if (isCurrentlyActive) {
            // Close this menu
            this.closeMenuItem(item, button);
        } else {
            if (isLevelZero) {
                // For level-0 items, close all other menus first
                this.closeAllMenus();
            } else {
                // For other levels, only close sibling menus in the same group
                const parentUl = item.closest('ul');
                const siblingItems = parentUl.querySelectorAll('.child-nav-desktop__item.has-child');
                siblingItems.forEach(sibling => {
                    if (sibling !== item) {
                        this.closeMenuItem(sibling, sibling.querySelector('button.childNavButton'));
                    }
                });
            }

            // Open this menu
            this.openMenuItem(item, button);
        }
    }

    openMenuItem(item, button) {
        const submenu = item.querySelector('ul');

        item.classList.add(CONFIG.classes.active);
        this.activeItems.add(item);

        if (button) {
            button.setAttribute('aria-expanded', 'true');
        }

        if (submenu) {
            submenu.setAttribute('aria-hidden', 'false');

            // Also show nested lists within this submenu when opened
            const nestedLists = submenu.querySelectorAll('.child-nav-desktop__nested');
            nestedLists.forEach(nestedList => {
                nestedList.setAttribute('aria-hidden', 'false');
            });
        }
    }

    closeMenuItem(item, button) {
        const submenu = item.querySelector('ul');

        item.classList.remove(CONFIG.classes.active);
        this.activeItems.delete(item);

        if (button) {
            button.setAttribute('aria-expanded', 'false');
        }

        if (submenu) {
            submenu.setAttribute('aria-hidden', 'true');

            // Also hide nested lists within this submenu when closed
            const nestedLists = submenu.querySelectorAll('.child-nav-desktop__nested');
            nestedLists.forEach(nestedList => {
                nestedList.setAttribute('aria-hidden', 'true');
            });
        }

        // Also close any nested open menus within this item
        const nestedActiveItems = item.querySelectorAll('.child-nav-desktop__item.has-child.' + CONFIG.classes.active);
        nestedActiveItems.forEach(nestedItem => {
            const nestedButton = nestedItem.querySelector('button.childNavButton');
            this.closeMenuItem(nestedItem, nestedButton);
        });
    }

    closeAllMenus() {
        this.activeItems.forEach(item => {
            const button = item.querySelector('button.childNavButton');
            this.closeMenuItem(item, button);
        });
    }

    setupReverseTabBehavior() {
        const desktopNav = document.querySelector(CONFIG.selectors.desktopNav);
        if (!desktopNav || !this.menuBtn) return;

        const firstLink = desktopNav.querySelector('.child-nav-desktop__link');
        if (!firstLink) return;

        firstLink.addEventListener('keydown', e => {
            // Check for Shift+Tab (backwards tab) and ensure user is actually tabbing
            if (e.key === 'Tab' && e.shiftKey && document.body.classList.contains('user-is-tabbing')) {
                e.preventDefault();
                this.menuBtn.focus();
            }
        });
    }

    setupForwardTabBehavior() {
        const desktopNav = document.querySelector(CONFIG.selectors.desktopNav);
        if (!desktopNav || !this.menuBtn) return;

        // Find all links in level-0 navigation (both navigation and footer links)
        const level0Nav = desktopNav.querySelector('.global-nav__list.global-nav--level-0, .child-nav-desktop--level-0');
        if (!level0Nav) return;

        // Get all anchor tags in level-0
        const allLinks = level0Nav.querySelectorAll('a');
        if (allLinks.length === 0) return;

        const lastLink = allLinks[allLinks.length - 1];

        lastLink.addEventListener('keydown', e => {
            // Check for forward Tab (no shift key) and ensure user is actually tabbing
            if (e.key === 'Tab' && !e.shiftKey && document.body.classList.contains('user-is-tabbing')) {
                e.preventDefault();
                this.menuBtn.focus();
            }
        });
    }

    setupMenuButton() {
        if (!this.menuBtn) return;

        this.menuBtn.addEventListener('click', () => {
            // Close all menus when main menu button is clicked
            this.closeAllMenus();
            
            // Focus the first link in the global nav desktop when menu is opened
            // Only if user is actively tabbing (keyboard navigation)
            if (document.body.classList.contains('user-is-tabbing')) {
                // Use a small delay to ensure the nav is visible/accessible
                setTimeout(() => {
                    const desktopNav = document.querySelector(CONFIG.selectors.desktopNav);
                    if (desktopNav) {
                        const firstLink = desktopNav.querySelector('.child-nav-desktop__link');
                        if (firstLink) {
                            firstLink.focus();
                        }
                    }
                }, 100);
            }
        });
    }
}

// Factory function for initialization
export function init() {
    const navigation = new GlobalNavigationDesktop();
    navigation.init();
}
